/**
 * AttachmentChips - Chips de anexos em staging do composer de tickets
 *
 * Visual compacto (protótipo T1: badge colorido por tipo + nome + tamanho),
 * com barra de progresso durante upload, thumbnail pra imagens, botão de
 * retry quando falha e ✕ pra remover. Consumido pelo `TicketEditor`.
 */

import React from 'react';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import type { ComposerAttachment } from '../../hooks/useTicketComposerAttachments';

export interface AttachmentChipsProps {
  attachments: ComposerAttachment[];
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes <= 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);
  return `${Math.round(value * 10) / 10} ${sizes[i]}`;
};

interface FileBadge {
  icon: IconName;
  background: string;
}

const getFileBadge = (file: File): FileBadge => {
  if (file.type.startsWith('image/')) return { icon: 'image', background: '#3b82f6' };
  if (file.type.startsWith('video/')) return { icon: 'video', background: '#a855f7' };
  if (file.type.startsWith('audio/')) return { icon: 'music', background: '#ec4899' };
  if (file.type === 'application/pdf') return { icon: 'file-text', background: '#ef4444' };
  return { icon: 'file', background: '#64748b' };
};

export const AttachmentChips: React.FC<AttachmentChipsProps> = ({ attachments, onRemove, onRetry }) => {
  if (attachments.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2" data-testid="ticket-composer-attachments">
      {attachments.map((attachment) => {
        const badge = getFileBadge(attachment.file);
        const isPendingOrUploading =
          attachment.uploadStatus === 'pending' || attachment.uploadStatus === 'uploading';
        const isFailed = attachment.uploadStatus === 'failed';
        const isCompleted = attachment.uploadStatus === 'completed';

        return (
          <div
            key={attachment.id}
            data-testid={`ticket-composer-attachment-${attachment.id}`}
            className="relative flex items-center gap-1.5 overflow-hidden rounded-lg border pl-1.5 pr-1.5 py-1 text-xs font-medium"
            style={{
              borderColor: isFailed ? 'var(--theme-error-color)' : 'var(--theme-card-border)',
              backgroundColor: isFailed ? 'rgba(239, 68, 68, 0.08)' : 'var(--theme-card-background)',
              color: 'var(--theme-card-text)',
            }}
            title={attachment.error || attachment.file.name}
          >
            {/* Badge/thumbnail */}
            {attachment.preview ? (
              <img
                src={attachment.preview}
                alt={attachment.file.name}
                className="w-5 h-5 rounded object-cover flex-shrink-0"
              />
            ) : (
              <span
                className="flex items-center justify-center rounded flex-shrink-0"
                style={{ width: 20, height: 20, backgroundColor: badge.background, color: '#fff' }}
              >
                <Icon name={badge.icon} className="w-3 h-3" />
              </span>
            )}

            {isPendingOrUploading && (
              <span
                className="absolute inset-0 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
              >
                <Icon name="loader" className="w-3.5 h-3.5 animate-spin" style={{ color: '#fff' }} />
              </span>
            )}

            <span className="max-w-[140px] truncate">{attachment.file.name}</span>

            <span style={{ color: 'var(--theme-muted-text)', fontWeight: 400 }}>
              {isPendingOrUploading
                ? `${attachment.uploadProgress}%`
                : isFailed
                ? 'Falhou'
                : formatFileSize(attachment.file.size)}
            </span>

            {isCompleted && <Icon name="check-circle" className="w-3.5 h-3.5" style={{ color: '#10B981' }} />}

            {isFailed && (
              <button
                type="button"
                onClick={() => onRetry(attachment.id)}
                data-testid={`ticket-composer-attachment-retry-${attachment.id}`}
                className="flex items-center justify-center rounded p-0.5"
                style={{ color: 'var(--theme-error-color)' }}
                title="Tentar novamente"
              >
                <Icon name="refresh" className="w-3.5 h-3.5" />
              </button>
            )}

            <button
              type="button"
              onClick={() => onRemove(attachment.id)}
              data-testid={`ticket-composer-attachment-remove-${attachment.id}`}
              className="flex items-center justify-center rounded p-0.5"
              style={{ color: 'var(--theme-muted-text)' }}
              title="Remover anexo"
            >
              <Icon name="x" className="w-3.5 h-3.5" />
            </button>

            {isPendingOrUploading && (
              <div
                className="absolute bottom-0 left-0 h-0.5 transition-all"
                style={{ width: `${attachment.uploadProgress}%`, backgroundColor: 'var(--theme-sidebar-active)' }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

AttachmentChips.displayName = 'AttachmentChips';
